import { useEffect, useRef } from "react";
import { getParticipantName, getSportTerminology } from "../utils/matchDisplayUtils";
import matchService from "../../../../services/api/matchService";
import { extractScoresFromMatchResponse, extractScoreVersion } from "../utils/scoreActionUtils";
import { mergeFeedEvents } from "../utils/liveScoreState";

// Match events that sync state rather than appear in the feed
const NON_FEED_EVENTS = [
  "score_update",
  "score_increase",
  "score_decrease",
  "live_score_update",
  "lineup_update",
  "game_state_update",
  "clock_update",
];

const useLiveMatchWebSocket = ({
  matchId,
  numericMatchId,
  socket,
  connected,
  connectionState,
  connect,
  joinMatch,
  leaveMatch,
  liveScore,
  matchEvents,
  match,
  isReadOnlyMode,
  setEventLog,
  setCurrentScore,
  announce,
  isScoreSyncPending,
  // (score, version) => boolean — adopts an authoritative server score as the
  // base for the next local write (useLiveMatchData); false = keep local score
  syncServerScore,
  // The whole live_score_update broadcast — its eventId and eventData
  // (points, side, undo) describe the change for the viewers' feed
  lastScoreBroadcast = null,
}) => {
  const prevScoreRef = useRef({ participant1: 0, participant2: 0 });
  const baselineSetRef = useRef(false);
  const lastBroadcastRef = useRef(lastScoreBroadcast);
  lastBroadcastRef.current = lastScoreBroadcast;

  // The score the page loaded with is the baseline: the first broadcast must
  // not look like a jump from 0-0 (one fake "scored" event for the whole score).
  useEffect(() => {
    if (baselineSetRef.current || !match) return;
    const loaded = extractScoresFromMatchResponse(match);
    if (loaded) prevScoreRef.current = loaded;
    baselineSetRef.current = true;
  }, [match]);

  useEffect(() => {
    if (!connected && connectionState === "disconnected") {
      connect();
    }
  }, [connected, connectionState, connect]);

  // Use the integer match.id for WS room joining so the room name matches
  // what the backend broadcasts to (match_330), regardless of the URL format.
  // Only join once the numeric ID is available to avoid creating phantom rooms.
  const wsMatchId = numericMatchId != null ? String(numericMatchId) : null;

  useEffect(() => {
    if (!wsMatchId || !socket || !connected || connectionState !== "connected") return;

    joinMatch(wsMatchId);

    return () => {
      leaveMatch(wsMatchId);
    };
  }, [wsMatchId, socket, connected, connectionState, joinMatch, leaveMatch]);

  // ── Polling fallback when WebSocket is disconnected ───────────────────────
  // Polls GET /matches/:id every 5 s so viewers always see the latest score
  // even when real-time sync is unavailable.  Stops as soon as WS reconnects.
  useEffect(() => {
    if (connected || !matchId) return;

    const poll = async () => {
      try {
        const res = await matchService.getMatchById(matchId);
        const polledScore = extractScoresFromMatchResponse(res.data);
        if (!polledScore) return;
        // Keep the scorer's write base in step with the server (LS-B05); an
        // editor's own pending write wins until it settles.
        if (syncServerScore && !syncServerScore(polledScore, extractScoreVersion(res.data))) return;

        setCurrentScore((prev) => {
          const p1Changed = prev.participant1 !== polledScore.participant1;
          const p2Changed = prev.participant2 !== polledScore.participant2;

          if (!p1Changed && !p2Changed) return prev;

          // Also push a feed event for view-only users
          [
            p1Changed && { key: 'participant1', prev: prev.participant1, next: polledScore.participant1 },
            p2Changed && { key: 'participant2', prev: prev.participant2, next: polledScore.participant2 },
          ].filter(Boolean).forEach(({ key, prev: oldScore, next: newScore }) => {
            const isIncrease = newScore > oldScore;
            const participantName = getParticipantName(match, key);
            const description = isIncrease
              ? `${getSportTerminology(match?.sport_name)} scored by ${participantName}`
              : `Score correction for ${participantName}`;

            // View-only users don't trigger their own announce() calls when
            // scoring, so announce polled score changes for them here.
            if (isReadOnlyMode && announce) {
              const opponentKey = key === 'participant1' ? 'participant2' : 'participant1';
              announce(newScore, polledScore[opponentKey] ?? 0, !isIncrease);
            }

            setEventLog((prevLog) => [
              {
                id: `poll-score-${Date.now()}-${key}-${Math.random()}`,
                type: isIncrease ? 'score_increase' : 'score_decrease',
                participant: key,
                participantName,
                score: newScore,
                description,
                hideParticipantName: true,
                timestamp: new Date().toLocaleTimeString(),
              },
              ...prevLog.slice(0, 49),
            ]);
          });

          return polledScore;
        });
      } catch {
        // silent — network errors shouldn't surface as UI errors in this path
      }
    };

    // Poll immediately on mount / WS drop, then every 5 s
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [connected, matchId, match, setCurrentScore, setEventLog, isReadOnlyMode, announce, syncServerScore]);

  useEffect(() => {
    if (liveScore && match) {
      const processedScore = {
        participant1:
          typeof liveScore.participant1 === "object"
            ? liveScore.participant1.total || 0
            : liveScore.participant1 || 0,
        participant2:
          typeof liveScore.participant2 === "object"
            ? liveScore.participant2.total || 0
            : liveScore.participant2 || 0,
      };

      const scoreChanged =
        processedScore.participant1 !== prevScoreRef.current.participant1 ||
        processedScore.participant2 !== prevScoreRef.current.participant2;

      if (scoreChanged) {
        let participantChanged = null;
        let changeType = null;

        if (processedScore.participant1 > prevScoreRef.current.participant1) {
          participantChanged = "participant1";
          changeType = "increase";
        } else if (processedScore.participant1 < prevScoreRef.current.participant1) {
          participantChanged = "participant1";
          changeType = "decrease";
        } else if (processedScore.participant2 > prevScoreRef.current.participant2) {
          participantChanged = "participant2";
          changeType = "increase";
        } else if (processedScore.participant2 < prevScoreRef.current.participant2) {
          participantChanged = "participant2";
          changeType = "decrease";
        }

        if (participantChanged && changeType && isReadOnlyMode) {
          // Speak the score change for view-only users (editors already
          // announce their own actions directly via announceScoreChange).
          if (announce) {
            const opponentKey = participantChanged === 'participant1' ? 'participant2' : 'participant1';
            announce(processedScore[participantChanged], processedScore[opponentKey] ?? 0, changeType === 'decrease');
          }

          // The broadcast's own eventId / points when it carries them; the
          // score difference otherwise (follow-up b: real points, not +1).
          const broadcast = lastBroadcastRef.current;
          const data = broadcast?.eventData || {};
          const sameBroadcast = !!broadcast && broadcast.scoreData === liveScore;
          const diff = processedScore[participantChanged] - (prevScoreRef.current[participantChanged] || 0);
          const points = sameBroadcast && Number.isFinite(Number(data.points)) && Number(data.points) !== 0
            ? Number(data.points)
            : diff;

          const eventDescription =
            changeType === "increase"
              ? `${getSportTerminology(match?.sport_name)} scored by ${getParticipantName(
                  match,
                  participantChanged
                )}`
              : `Score correction for ${getParticipantName(match, participantChanged)}`;

          const feedEvent = {
            id: (sameBroadcast && broadcast.eventId) || `websocket-score-${Date.now()}-${Math.random()}`,
            eventId: (sameBroadcast && broadcast.eventId) || null,
            clientEventId: (sameBroadcast && data.clientEventId) || null,
            type: `score_${changeType}`,
            participant: participantChanged,
            participantName: getParticipantName(match, participantChanged),
            score: processedScore[participantChanged],
            points,
            sportTerminology: getSportTerminology(match?.sport_name),
            description: eventDescription,
            hideParticipantName: true,
            timestamp: new Date().toLocaleTimeString(),
          };
          setEventLog((prev) => mergeFeedEvents(prev, [feedEvent]));
        }

        prevScoreRef.current = processedScore;
        // The scorer's own local score is authoritative while its updates are
        // still being sent — an echo of an older score must not roll it back.
        if (!isReadOnlyMode && isScoreSyncPending && isScoreSyncPending()) return;
        // Adopt it as the base for this device's next write too, unless it is
        // older than the version we already hold (LS-B05).
        if (syncServerScore && !syncServerScore(processedScore, extractScoreVersion(liveScore))) return;
        setCurrentScore(processedScore);
      }
    }
  }, [liveScore, match, isReadOnlyMode, setCurrentScore, setEventLog, announce, isScoreSyncPending, syncServerScore]);

  useEffect(() => {
    if (matchEvents && matchEvents.length > 0) {
      const nonScoreEvents = matchEvents.filter((event) => {
        const eventType = event.eventType || event.event_type || event.type;
        return !NON_FEED_EVENTS.includes(eventType);
      });

      if (nonScoreEvents.length > 0) {
        // Dedupe on the event's identity only: the server eventId, or the
        // clientEventId this device gave its own event (LS-B12). Two events
        // of the same type in the same second are both kept.
        const incoming = nonScoreEvents.map((event) => {
            const eventType = event.eventType || event.event_type || event.type;
            const eventData = event.eventData || event.data || {};
            const timestamp = event.timestamp || new Date().toISOString();
            const eventId =
              event.eventId || event.id || eventData.clientEventId || `${eventType}_${timestamp}`;

            return {
              id: eventId,
              eventId: event.eventId || null,
              clientEventId: eventData.clientEventId || null,
              type: eventType,
              participant: eventData.participantKey || eventData.participant || "system",
              participantName: eventData.participantName || "System",
              description:
                eventData.enhancedDescription ||
                eventData.description ||
                `${eventType} event`,
              timestamp: new Date(timestamp).toLocaleTimeString(),
              hideParticipantName:
                eventType === "timeout" ||
                eventType === "timeout_end" ||
                eventType === "match_ended",
              // Preserve advanced_score fields for remote viewers
              ...(eventType === "advanced_score" ? {
                playerName:   eventData.playerName,
                playerUserId: eventData.playerUserId || null,
                metricKey:    eventData.metricKey,
                metricLabel:  eventData.metricLabel,
                affectsScore: eventData.affectsScore,
                isPositive:   eventData.isPositive,
                scorePoints:  eventData.scorePoints ?? eventData.points,
              } : {}),
            };
        });
        // matchEvents is newest first: merge oldest first so the newest ends on top
        setEventLog((prev) => [...incoming].reverse().reduce((log, e) => mergeFeedEvents(log, [e]), prev));
      }
    }
  }, [matchEvents, setEventLog]);
};

export default useLiveMatchWebSocket;
